import type { StoryMetadata, StorySeries } from '@/content/stories'
import type { LearningStatus } from './learning-state-core'
import { getStoryProgressKind } from './content-progress-core'

export type StorySeriesProgressLike = {
  status?: LearningStatus | null
  progress?: number | null
}

export type StorySeriesChapterSummary = StoryMetadata & {
  progressStatus: 'new' | 'started' | 'read'
  isNextChapter: boolean
}

export type StorySeriesSummary = {
  series: StorySeries
  chapters: StorySeriesChapterSummary[]
  readCount: number
  startedCount: number
  nextChapter: StorySeriesChapterSummary | null
  currentChapter: StorySeriesChapterSummary | null
  primaryChapter: StorySeriesChapterSummary | null
}

function chapterProgressStatus(
  progress: StorySeriesProgressLike | null | undefined
): StorySeriesChapterSummary['progressStatus'] {
  return getStoryProgressKind(progress)
}

export function buildStorySeriesSummaries({
  seriesRegistry,
  stories,
  progressBySlug = new Map()
}: {
  seriesRegistry: StorySeries[]
  stories: StoryMetadata[]
  progressBySlug?: Map<string, StorySeriesProgressLike>
}): StorySeriesSummary[] {
  const storyBySlug = new Map(stories.map((story) => [story.slug, story]))

  return seriesRegistry
    .map((series) => {
      const orderedStories = series.chapterSlugs
        .map((slug) => storyBySlug.get(slug))
        .filter((story): story is StoryMetadata => Boolean(story))

      const chapterStatuses = orderedStories.map((story) =>
        chapterProgressStatus(progressBySlug.get(story.slug))
      )
      const activeIndex = chapterStatuses.findIndex(
        (status) => status === 'started'
      )
      const latestProgressIndex = chapterStatuses.reduce(
        (latest, status, index) =>
          status === 'started' || status === 'read' ? index : latest,
        -1
      )
      const nextUnreadSearchStart =
        activeIndex >= 0 ? activeIndex : Math.max(0, latestProgressIndex + 1)
      const nextUnreadIndex = chapterStatuses.findIndex(
        (status, index) => index >= nextUnreadSearchStart && status !== 'read'
      )

      const chapters = orderedStories.map((story, index) => ({
        ...story,
        progressStatus: chapterStatuses[index],
        isNextChapter: index === nextUnreadIndex
      }))
      const currentChapter =
        activeIndex >= 0 ? (chapters[activeIndex] ?? null) : null
      const nextChapter =
        nextUnreadIndex >= 0 ? (chapters[nextUnreadIndex] ?? null) : null

      return {
        series: {
          ...series,
          chapterSlugs: [...series.chapterSlugs]
        },
        chapters,
        readCount: chapters.filter(
          (chapter) => chapter.progressStatus === 'read'
        ).length,
        startedCount: chapters.filter(
          (chapter) => chapter.progressStatus === 'started'
        ).length,
        nextChapter,
        currentChapter,
        primaryChapter: currentChapter ?? nextChapter
      }
    })
    .filter((summary) => summary.chapters.length > 0)
}
